// ============================================================================
// RealTimeResultsSheet - per-player session results at a cash table
// ============================================================================
// The CoinPoker-style "Real Time Results" panel: every active player with
// their total session buy-in and current winnings (stack minus buy-in),
// green for up, red for down. Buy-ins come from table.sessionBuyIns -
// written at join, incremented on every reload, never synced to the stack
// (playerBuyIns is, which is why it cannot answer "how much did I put in").
// ============================================================================

interface ResultRow {
  id: string;
  name: string;
  buyIn: number | null; // null = unknown (seated before this feature shipped)
  chips: number;
  cashedOut?: number;   // session tables: stack taken when leaving (ledger)
  left?: boolean;       // departed player kept on the settle-up sheet
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  rows: ResultRow[];
  currency: 'playcoins' | 'pkcash';
  sessionMode?: boolean; // host-banked home game: this panel IS the settle-up sheet
}

const RealTimeResultsSheet = ({ isOpen, onClose, rows, currency, sessionMode = false }: Props) => {
  if (!isOpen) return null;

  const money = (n: number) => currency === 'pkcash'
    ? n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : Math.round(n).toLocaleString('en-US');

  // Net = current stack + anything taken off the table - everything put in.
  // On host-banked tables this is the settle-up number and sums to zero
  // across all rows (assignments are net-neutral by construction).
  const withWinnings = rows.map(r => ({
    ...r,
    winnings: r.buyIn === null ? null
      : Math.round((r.chips + (r.cashedOut || 0) - r.buyIn) * 100) / 100,
  })).sort((a, b) => (b.winnings ?? -Infinity) - (a.winnings ?? -Infinity));

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9200,
        background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '420px', maxHeight: '80vh', overflowY: 'auto',
          background: '#0f1722', border: '1px solid rgba(0,217,217,0.35)',
          borderRadius: '16px', padding: '20px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
          fontFamily: "Inter,'Helvetica Neue',Arial,sans-serif",
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
          <h2 style={{ color: '#fff', margin: 0, fontSize: '18px', fontWeight: 700 }}>Real Time Results</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none', border: 'none', color: '#94a3b8',
              fontSize: '22px', cursor: 'pointer', lineHeight: 1, padding: '4px',
            }}
          >
            &times;
          </button>
        </div>
        <div style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '14px' }}>
          Active Players ({rows.length})
        </div>

        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 80px 96px',
          padding: '8px 10px', color: '#64748b', fontSize: '12px', fontWeight: 600,
          borderBottom: '1px solid rgba(148,163,184,0.15)',
        }}>
          <span>Player</span>
          <span style={{ textAlign: 'right' }}>Buy-In</span>
          <span style={{ textAlign: 'right' }}>{sessionMode ? 'Net' : 'Chips won'}</span>
        </div>

        {withWinnings.map(r => (
          <div key={r.id} style={{
            display: 'grid', gridTemplateColumns: '1fr 80px 96px',
            padding: '12px 10px', alignItems: 'center',
            borderBottom: '1px solid rgba(148,163,184,0.08)',
          }}>
            <span style={{
              color: '#e2e8f0', fontSize: '14px', fontWeight: 600,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {r.name}{r.left ? <span style={{ color: '#64748b', fontWeight: 500 }}> (left)</span> : null}
            </span>
            <span style={{ color: '#cbd5e1', fontSize: '14px', textAlign: 'right' }}>
              {r.buyIn === null ? '-' : money(r.buyIn)}
            </span>
            <span style={{
              fontSize: '14px', fontWeight: 700, textAlign: 'right',
              color: r.winnings === null ? '#64748b'
                : r.winnings > 0 ? '#4ade80'
                : r.winnings < 0 ? '#f87171' : '#cbd5e1',
            }}>
              {r.winnings === null ? '-'
                : `${r.winnings > 0 ? '+' : ''}${money(r.winnings)}`}
            </span>
          </div>
        ))}

        {sessionMode && rows.length > 0 && (
          <div style={{ color: '#94a3b8', fontSize: '13px', padding: '16px 10px 4px', textAlign: 'center', lineHeight: 1.5 }}>
            Home game: nets always sum to zero across everyone. Chips are
            session-only scorekeeping with no cash value.
          </div>
        )}
        {rows.length === 0 && (
          <div style={{ color: '#64748b', fontSize: '13px', padding: '20px 10px', textAlign: 'center' }}>
            No active players
          </div>
        )}
      </div>
    </div>
  );
};

export default RealTimeResultsSheet;
